import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { ToasterService } from '@app/shared/services/toaster.service';

import { FinanceService, TierRateRow } from '../../finance.service';
import {
  TierRateModalComponent,
  TierRateModalData,
  TierRateModalResult,
} from './tier-rate-modal.component';

/**
 * Tier Rates admin tab on Finance Details. Lists the finance_tier_rate
 * master table for a chosen year so finance admins can keep Base Rate +
 * Office Exp Recovery current. Picking a Tier Model on the Edit Project
 * page reads from this same master to prefill the resource's Base Rate.
 *
 * Standalone component — mirrors finance-rate-card so the parent module
 * just imports the symbol.
 */
@Component({
  selector: 'app-finance-tier-rates',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './finance-tier-rates.component.html',
  styleUrl: './finance-tier-rates.component.scss',
})
export class FinanceTierRatesComponent implements OnInit {
  /** Picker options — 5 prior + current + 1 future. */
  readonly yearOptions: number[] = (() => {
    const now = new Date().getFullYear();
    const years: number[] = [];
    for (let y = now + 1; y >= now - 5; y--) years.push(y);
    return years;
  })();

  /** Master table only carries Tier Model + Base Rate now — Office Exp,
   *  Project Cost, and D&T Cost Hourly are all formula-driven per Product
   *  spec (location/PO/POI-keyed, not per-tier) so they no longer belong
   *  on this admin surface. */
  readonly displayedColumns: string[] = [
    'tierModel',
    'baseRate',
    'actions',
  ];

  selectedYear: number = new Date().getFullYear();
  rows: TierRateRow[] = [];
  loading = false;
  errorMessage = '';

  constructor(
    private readonly financeService: FinanceService,
    private readonly dialog: MatDialog,
    private readonly toaster: ToasterService,
    private readonly router: Router,
  ) {}

  backToDashboard(): void {
    this.router.navigate(['/finance']);
  }

  backToFinanceDetails(): void {
    this.router.navigate(['/finance/details']);
  }

  ngOnInit(): void {
    this.load();
  }

  onYearChange(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.errorMessage = '';
    this.financeService.listTierRates(this.selectedYear).subscribe({
      next: (rows) => {
        this.rows = rows;
        this.loading = false;
      },
      error: () => {
        this.errorMessage = 'Unable to load tier rates. Please try again.';
        this.loading = false;
      },
    });
  }

  openAdd(): void {
    this.openModal({ year: this.selectedYear });
  }

  openEdit(row: TierRateRow): void {
    this.openModal({ year: this.selectedYear, row });
  }

  delete(row: TierRateRow): void {
    const ok = globalThis.confirm(
      `Delete tier rate "${row.tierModel}" for ${row.targetYear}? `
      + 'Existing assignment rate-card values are unaffected.',
    );
    if (!ok) return;
    this.financeService.deleteTierRate(row.id).subscribe({
      next: () => {
        this.toaster.showSuccess('Tier rate deleted.', 'Success');
        this.load();
      },
      error: () => this.toaster.showError('Unable to delete tier rate.', 'Error'),
    });
  }

  trackById = (_: number, row: TierRateRow) => row.id;

  private openModal(data: TierRateModalData): void {
    const ref = this.dialog.open<TierRateModalComponent, TierRateModalData, TierRateModalResult | undefined>(
      TierRateModalComponent,
      {
        data,
        autoFocus: 'first-tabbable',
        restoreFocus: true,
        width: '480px',
        panelClass: 'finance-tier-rate-modal-panel',
      },
    );

    ref.afterClosed().subscribe((result) => {
      if (!result) return;
      const obs = result.id
        ? this.financeService.updateTierRate(result.id, result.payload)
        : this.financeService.createTierRate(result.payload);
      obs.subscribe({
        next: () => {
          this.toaster.showSuccess(result.id ? 'Tier rate updated.' : 'Tier rate added.', 'Success');
          // If the operator saved a row in a different year, jump there.
          if (result.payload.targetYear !== this.selectedYear) {
            this.selectedYear = result.payload.targetYear;
          }
          this.load();
        },
        error: (err) => {
          const msg = err?.error?.message || 'Unable to save tier rate.';
          this.toaster.showError(msg, 'Error');
        },
      });
    });
  }
}
